import { EnvironmentInjector, Injectable, runInInjectionContext } from '@angular/core';
import {
	CollectionReference,
	DocumentData,
	DocumentReference,
	Firestore,
	Query,
	collection,
	collectionSnapshots,
	doc,
	getDoc,
	getDocs,
	limit,
	orderBy,
	query,
	setDoc,
	where,
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MasScheduleConverterService } from '../converters';
import { Account, MASScheduleExtrasModel, MASScheduleModel, Notifications, Schedule } from '../models';
import { chunkForFirestoreIn } from '@common/domain/firestore-in-query';
import { resolveScheduleType } from '@common/domain/schedule-type-registry';
import type { ScheduleType } from '@common/domain/schedule-types';
import { AdvancedDebugging, DateTimeUtils } from '../utilities';

@Injectable({
	providedIn: 'root',
})
export class MAS_Schedules_Service {
	public selectedNode!: Account;
	private readonly advancedDebugging = new AdvancedDebugging();

	constructor(
		public afs: Firestore,
		private injector: EnvironmentInjector,
		private scheduleConverter: MasScheduleConverterService,
	) {}

	/** AngularFire requires an active injection context for eager reads (e.g. after `await` in UI). */
	private runInFirestoreContext<T>(fn: () => Promise<T>): Promise<T> {
		return runInInjectionContext(this.injector, fn);
	}

	private failSchedules(operation: string, error: unknown, context: Record<string, unknown> = {}): never {
		this.advancedDebugging.captureError(operation, error, { tags: { operation }, ...context });
		throw error;
	}

	/**
	 * Writes a schedule document by id. Wrapped in `runInInjectionContext` so
	 * AngularFire `getDoc` / `setDoc` run with a valid injector (avoids warnings
	 * and zone issues when called from async UI handlers after `await`).
	 */
	async persistScheduleRaw(payload: Schedule, merge: boolean = true): Promise<Schedule> {
		const err = new Error(
			`Client mas-schedules writes are disabled. Use enrollInScheduleAtomic or a schedules/* server endpoint. scheduleId=${payload.id}`,
		);
		this.failSchedules('mas:MAS_Schedules_Service.persistScheduleRaw.disabled', err, {
			merge,
			scheduleId: payload.id,
		});
	}

	async getScheduleById(id: string): Promise<MASScheduleExtrasModel> {
		try {
			return await this.runInFirestoreContext(async () => {
				const docRef = doc(this.afs, 'mas-schedules', id) as DocumentReference<Schedule>;
				const snap = await getDoc(docRef);

				if (!snap.exists()) {
					throw new Error(`Schedule with id "${id}" not found`);
				}

				const data = snap.data(); // Schedule

				// Convert raw Schedule to MASScheduleModel (same as your list method)
				const model = this.scheduleConverter.fromRaw({
					...(data as any),
					id: snap.id, // ensure model.id matches doc id
				});

				const extrasModel = new MASScheduleExtrasModel(model);

				// Preformatted times like in getSchedulesByDateRangeAndLimit
				extrasModel.startTime = DateTimeUtils.formatDate(data.start.dateTime, 'time12');
				extrasModel.endTime = DateTimeUtils.formatDate(data.end.dateTime, 'time12');

				return extrasModel;
			});
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.getScheduleById', error, { scheduleId: id });
		}
	}

	/**
	 * @deprecated Use getScheduleModelsByProgramAndDateRange instead.
	 */
	async getSchedulesByProgramAndDateRange(programs: string[], startDate: string, endDate: string) {
		if (programs.length === 0) return [];

		try {
			return await this.runInFirestoreContext(async () => {
				const coll = collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>;
				const merged = await this.queryScheduleDocsInBatches(programs, batch =>
					query<Schedule, DocumentData>(
						coll,
						where('summary', 'in', batch),
						where('start.dateTime', '>', startDate),
						where('start.dateTime', '<', endDate),
						orderBy('start.dateTime'),
					),
				);
				merged.sort((a, b) => String(a.start?.dateTime ?? '').localeCompare(String(b.start?.dateTime ?? '')));
				return merged;
			});
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.getSchedulesByProgramAndDateRange', error, { programCount: programs.length });
		}
	}

	async getScheduleModelsByProgramAndDateRange(programs: string[], startDate: string, endDate: string) {
		if (programs.length === 0) return [];

		try {
			return await this.runInFirestoreContext(async () => {
				const rows = await this.getSchedulesByProgramAndDateRange(programs, startDate, endDate);
				return rows.map(row => this.scheduleConverter.fromRaw(row));
			});
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.getScheduleModelsByProgramAndDateRange', error, {
				programCount: programs.length,
			});
		}
	}

	async getSchedulesNotClosed(endDate: string, programs: string[]) {
		if (programs.length === 0) return [];
		try {
			return await this.runInFirestoreContext(async () => {
				const coll = collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>;
				return this.queryScheduleDocsInBatches(programs, batch =>
					query<Schedule, DocumentData>(coll, where('eventType', '==', 'default'), where('summary', 'in', batch), where('end.dateTime', '<', endDate)),
				);
			});
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.getSchedulesNotClosed', error, { programCount: programs.length });
		}
	}

	/**
	 * @deprecated Use getFutureScheduleByProgramName instead.
	 */
	async getSchedulesByProgramAsPromise(programName: string) {
		return this.runInFirestoreContext(async () => {
			const q = query(collection(this.afs, 'mas-schedules'), where('summary', '==', programName), where('end.dateTime', '>', DateTimeUtils.formatDate(new Date(), 'local')));
			const qDocs = await getDocs(q);
			const qData = qDocs.docs.map(m => m.data());

			const schedules = qData.map(m => {
				m['startDateTime'] = DateTimeUtils.formatDate(m['start'].dateTime as unknown as Date, 'friendly', 0);
				return m;
			});

			return schedules;
		});
	}

	async getFutureScheduleByProgramName(
		programName: string,
		options?: { scheduleTypes?: ScheduleType[] },
	): Promise<MASScheduleExtrasModel[]> {
		try {
			return await this.runInFirestoreContext(async () => {
				const now = new Date();
				const cutoff = new Date(now);
				const isLocalhost = window.location.hostname === 'localhost';

				// Subtract 30 days if on localhost
				if (isLocalhost) cutoff.setDate(now.getDate() - 30);

				const q = query<Schedule, DocumentData>(
					collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>,
					where('summary', '==', programName),
					where('end.dateTime', '>', DateTimeUtils.formatDate(cutoff, 'local'))
				);

				if (isLocalhost) {
					this.advancedDebugging.log('mas:MAS_Schedules_Service.getFutureScheduleByProgramName.cutoff', { cutoff });
				}

				const qDocs = await getDocs(q);
				const qData = qDocs.docs
					.map(m => this.scheduleConverter.fromRaw(m.data()))
					.filter(row => this.matchesScheduleTypeFilter(row, options?.scheduleTypes));
				return qData.map(m => new MASScheduleExtrasModel(m));
			});
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.getFutureScheduleByProgramName', error, {
				programName,
				scheduleTypes: options?.scheduleTypes?.join(',') ?? '',
			});
		}
	}

	private matchesScheduleTypeFilter(
		schedule: { eventType?: string; programId?: string },
		scheduleTypes: readonly ScheduleType[] | undefined,
	): boolean {
		if (!scheduleTypes?.length) {
			return true;
		}
		const canonicalType = resolveScheduleType(schedule.eventType ?? '', { programId: schedule.programId });
		return scheduleTypes.includes(canonicalType);
	}

	async getFutureScheduleByProgramId(programId: string): Promise<MASScheduleExtrasModel[]> {
		try {
			return await this.runInFirestoreContext(async () => {
				const now = new Date();
				const cutoff = new Date(now);
				const isLocalhost = window.location.hostname === 'localhost';

				// Subtract 30 days if on localhost
				if (isLocalhost) cutoff.setDate(now.getDate() - 30);

				const q = query<Schedule, DocumentData>(
					collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>,
					where('programId', '==', programId),
					where('end.dateTime', '>', DateTimeUtils.formatDate(cutoff, 'local'))
				);

				if (isLocalhost) {
					this.advancedDebugging.log('mas:MAS_Schedules_Service.getFutureScheduleByProgramId.cutoff', { cutoff });
				}

				const qDocs = await getDocs(q);
				const qData = qDocs.docs.map(m => this.scheduleConverter.fromRaw(m.data()));
				return qData.map(m => new MASScheduleExtrasModel(m));
			});
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.getFutureScheduleByProgramId', error, { programId });
		}
	}

	getSchedulesByCycleAndProgram(cycle: string, program: string) {
		return collectionSnapshots<Schedule>(
			query<Schedule, DocumentData>(
				collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>,
				where('cycleName', '==', cycle),
				where('summary', '==', program),
				orderBy('start.dateTime')
			)
		).pipe(
			map(changes => {
				return changes.map(a => {
					const data = a.data();
					data.id = a.id;
					return data;
				});
			})
		);
	}

	async getSchedulesByCycleName(cycleName: string): Promise<Schedule[]> {
		try {
			return await this.runInFirestoreContext(async () => {
				const q = query<Schedule, DocumentData>(
					collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>,
					where('cycleName', '==', cycleName),
					orderBy('start.dateTime'),
				);
				const qDocs = await getDocs(q);
				return qDocs.docs.map(snap => {
					const data = snap.data();
					data.id = snap.id;
					return data;
				});
			});
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.getSchedulesByCycleName', error, { cycleName });
		}
	}

	/**
	 * Schedules overlapping a visible calendar range (string compare on MAS `dateTime` shape).
	 * Query: `start < rangeEnd`, then filter `end > rangeStart` client-side.
	 */
	async getSchedulesOverlappingDateRange(startBound: string, endBound: string): Promise<Schedule[]> {
		try {
			return await this.runInFirestoreContext(async () => {
				const collectionRef = collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>;
				const q = query<Schedule, DocumentData>(
					collectionRef,
					where('start.dateTime', '<', endBound),
					orderBy('start.dateTime'),
				);
				const querySnapshot = await getDocs(q);
				return querySnapshot.docs
					.map(snap => {
						const data = snap.data();
						data.id = snap.id;
						return data;
					})
					.filter(row => String(row.end?.dateTime ?? '') > startBound);
			});
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.getSchedulesOverlappingDateRange', error, { startBound, endBound });
		}
	}

	/** @deprecated Use {@link getSchedulesOverlappingDateRange}. */
	async getSchedulesByStartDateRange(startBound: string, endBound: string): Promise<Schedule[]> {
		return this.getSchedulesOverlappingDateRange(startBound, endBound);
	}

	async getSchedulesAsPromise(ignorePast: boolean = false): Promise<Schedule[]> {
		try {
			return await this.runInFirestoreContext(async () => {
				const collectionRef = collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>;
				const q: Query<Schedule> = ignorePast
					? query<Schedule, DocumentData>(collectionRef, where('end.dateTime', '>', DateTimeUtils.formatDate(new Date(), 'local')))
					: query<Schedule, DocumentData>(collectionRef);
				const querySnapshot = await getDocs(q);
				return querySnapshot.docs.map(doc => doc.data());
			});
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.getSchedulesAsPromise', error, { ignorePast });
		}
	}

	/** Future facility event rows on `mas-schedules` (my Info → Events). */
	getFutureEventSchedulesAsObservable(): Observable<Schedule[]> {
		const nowLocal = DateTimeUtils.formatDate(new Date(), 'local');
		return collectionSnapshots<Schedule>(
			query<Schedule, DocumentData>(
				collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>,
				where('end.dateTime', '>', nowLocal),
				orderBy('end.dateTime'),
			),
		).pipe(
			map(changes =>
				changes
					.map(snap => {
						const data = snap.data();
						data.id = snap.id;
						return data;
					})
					.filter(row => this.matchesScheduleTypeFilter(row, ['event'])),
			),
		);
	}

	/** Future `mas-schedules` rows with `eventType: lesson` (my Programs → Lessons). */
	getFutureLessonSchedulesAsObservable(): Observable<Schedule[]> {
		const nowLocal = DateTimeUtils.formatDate(new Date(), 'local');
		return collectionSnapshots<Schedule>(
			query<Schedule, DocumentData>(
				collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>,
				where('end.dateTime', '>', nowLocal),
				orderBy('end.dateTime'),
			),
		).pipe(
			map(changes =>
				changes
					.map(snap => {
						const data = snap.data();
						data.id = snap.id;
						return data;
					})
					.filter(row => (row.eventType ?? '') === 'lesson'),
			),
		);
	}

	async getSchedulesWithMembersAttendedAsPromise(id: string) {
		try {
			return await this.runInFirestoreContext(async () => {
				const q = query<Schedule, DocumentData>(collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>, where('id', '>', id));
				return (await getDocs(q)).docs.map(m => m.data());
			});
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.getSchedulesWithMembersAttendedAsPromise', error, { afterId: id });
		}
	}

	getSchedulesByDateRangeAndLimit(startDate: string, endDate: string, numberOfSchedules = 1): Observable<MASScheduleExtrasModel[]> {
		return collectionSnapshots<Schedule>(
			query<Schedule, DocumentData>(
				collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>,
				where('end.dateTime', '>', startDate),
				where('end.dateTime', '<', endDate),
				orderBy('end.dateTime'),
				limit(numberOfSchedules)
			)
		).pipe(
			map(changes => {
				return changes.map(a => {
					const data = a.data() as Schedule;

					const model = this.scheduleConverter.fromRaw({
						...(data as any),
						id: a.id,
					});

					const extrasModel = new MASScheduleExtrasModel(model);

					// Preserve the old DisplaySchedule behavior: preformatted times
					extrasModel.startTime = DateTimeUtils.formatDate(data.start.dateTime, 'time12');
					extrasModel.endTime = DateTimeUtils.formatDate(data.end.dateTime, 'time12');

					return extrasModel;
				});
			})
		);
	}

	getSchedulesByProgramAsObservable(program: string) {
		const d = new Date();
		const givenDate = DateTimeUtils.formatDate(d, 'date');

		return collectionSnapshots<Schedule>(
			query<Schedule, DocumentData>(
				collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>,
				where('summary', '==', program),
				where('start.dateTime', '>=', givenDate),
				orderBy('start.dateTime')
			)
		).pipe(
			map(changes => {
				return changes.map(a => {
					const data = a.data();
					data.id = a.id;
					return data;
				});
			})
		);
	}

	async getSchedulesByProgramForChart(program: string, afterDateISO?: string) {
		try {
			return await this.runInFirestoreContext(async () => {
				const now = new Date();

				const afterDate = afterDateISO ? new Date(afterDateISO) : new Date(now.setMonth(now.getMonth() - 3));

				const formattedAfterDate = afterDate.toISOString();

				const q = query<Schedule, DocumentData>(
					collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>,
					where('summary', '==', program),
					where('start.dateTime', '<', new Date().toISOString()),
					where('start.dateTime', '>', formattedAfterDate),
					orderBy('start.dateTime')
				);

				const qDocs = await getDocs(q);
				if (!qDocs) return [];

				return qDocs.docs.map(m => m.data());
			});
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.getSchedulesByProgramForChart', error, { program });
		}
	}

	getSchedulesByReference(fld: string, operator: any, condition: any) {
		return collectionSnapshots<any>(query<any, DocumentData>(collection(this.afs, 'mas-schedules') as CollectionReference<any>, where(fld, operator, condition))).pipe(
			map(changes => {
				return changes.map(c => {
					const data = c.data() as any;
					data.id = c.id;
					return data;
				});
			})
		);
	}

	async getSchedulesByTestCategories(testCategories: string[]) {
		if (testCategories.length === 0) return [];

		try {
			return await this.runInFirestoreContext(async () => {
				const coll = collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>;
				const afterEnd = DateTimeUtils.formatDate(new Date(), 'local');
				const rows = await this.queryScheduleDocsInBatches(testCategories, batch =>
					query<Schedule, DocumentData>(coll, where('summary', 'in', batch), where('end.dateTime', '>', afterEnd)),
				);
				return rows.map(data => ({
					...data,
					startDateTime: DateTimeUtils.formatDate(data.start.dateTime, 'friendly'),
				})) as Schedule[];
			});
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.getSchedulesByTestCategories', error, {
				categoryCount: testCategories.length,
			});
		}
	}

	async getSchedulesForTesting(cycle: string, programs: string[]): Promise<Schedule[]> {
		if (programs.length === 0) return Promise.resolve([]);
		try {
			const coll = collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>;
			return await runInInjectionContext(this.injector, () =>
				this.queryScheduleDocsInBatches(programs, batch =>
					query<Schedule, DocumentData>(coll, where('cycleName', '==', cycle), where('summary', 'in', batch)),
				),
			);
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.getSchedulesForTesting', error, { cycle, programCount: programs.length });
		}
	}

	async getSchedulesForYAS() {
		try {
			return await this.runInFirestoreContext(async () => {
				const q = query<Schedule, DocumentData>(collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>, where('summary', '==', 'YAS'), where('end.dateTime', '>', '2023-06-01'));
				return (await getDocs(q)).docs.map(m => m.data());
			});
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.getSchedulesForYAS', error);
		}
	}

	async getSchedulesToUpdate(program: string) {
		try {
			return await this.runInFirestoreContext(async () => {
				const d = new Date();
				const givenDate = DateTimeUtils.formatDate(d, 'date');

				const q = query<Schedule, DocumentData>(collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>, where('summary', '==', program), where('start.dateTime', '>=', givenDate));
				const qDocs = await getDocs(q);
				if (!qDocs) return [];
				return qDocs.docs.map(m => m.data());
			});
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.getSchedulesToUpdate', error, { program });
		}
	}

	async getSchedulesInProgramsForUpdate(programs: string[]): Promise<MASScheduleModel[]> {
		if (programs.length === 0) return [];

		try {
			return await this.runInFirestoreContext(async () => {
				const d = new Date();
				const givenDate = DateTimeUtils.formatDate(d, 'date');
				const coll = collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>;
				const rows = await this.queryScheduleDocsInBatches(programs, batch =>
					query<Schedule, DocumentData>(coll, where('programId', 'in', batch), where('start.dateTime', '>=', givenDate)),
				);
				return rows.map(row => this.scheduleConverter.fromRaw(row));
			});
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.getSchedulesInProgramsForUpdate', error, { programCount: programs.length });
		}
	}

	async findExistingSchedule(each: Schedule): Promise<boolean> {
		try {
			return await this.runInFirestoreContext(async () => {
				const q = query<Schedule, DocumentData>(
					collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>,
					where('summary', '==', each.summary),
					where('start.dateTime', '==', each.start.dateTime),
					where('end.dateTime', '==', each.end.dateTime)
				);
				const count = await getDocs(q).then(docs => docs.size);
				return count > 0 ? true : false;
			});
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.findExistingSchedule', error, { summary: each.summary });
		}
	}

	async getSchedulesByPropertiesAsPromise(searchArray: Notifications[]): Promise<Schedule[]> {
		try {
			if (searchArray.length === 0) return [];

			if (window.location.hostname === 'localhost') {
				this.advancedDebugging.log('mas:MAS_Schedules_Service.getSchedulesByPropertiesAsPromise', {
					notificationCount: searchArray.length,
				});
			}

			return await this.runInFirestoreContext(async () => {
				const q = query<Schedule, DocumentData>(collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>, where('notifications', 'array-contains-any', searchArray));

				const querySnapshot = await getDocs(q);
				return querySnapshot.docs.map(m => m.data());
			});
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.getSchedulesByPropertiesAsPromise', error, {
				notificationCount: searchArray.length,
			});
		}
	}

	async getSchedulesWithNotifications() {
		try {
			return await this.runInFirestoreContext(async () => {
				const today = new Date();
				today.setHours(today.getHours() - 4);
				const tomorrow = new Date();
				tomorrow.setDate(tomorrow.getDate() + 2);
				const q = query<Schedule, DocumentData>(
					collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>,
					where('start.dateTime', '>', today.toISOString().slice(0, 10)),
					where('start.dateTime', '<', tomorrow.toISOString().slice(0, 10))
				);
				return (await getDocs(q)).docs.map(m => m.data());
			});
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.getSchedulesWithNotifications', error);
		}
	}

	/** Upcoming schedules for a program as view-model extras (MAS accounts reservations). */
	async loadScheduleExtrasForProgram(programId: string): Promise<MASScheduleExtrasModel[]> {
		try {
			return await this.runInFirestoreContext(async () => {
				const now = new Date();
				const cutoff = new Date(now);
				const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
				if (isLocalhost) {
					cutoff.setDate(now.getDate() - 30);
				}

				const q = query<Schedule, DocumentData>(
					collection(this.afs, 'mas-schedules') as CollectionReference<Schedule>,
					where('programId', '==', programId),
					where('end.dateTime', '>', DateTimeUtils.formatDate(cutoff, 'local')),
				);

				const qDocs = await getDocs(q);
				return qDocs.docs.map(docSnap => {
					const model = this.scheduleConverter.fromRaw({
						...(docSnap.data() as Schedule),
						id: docSnap.id,
					});
					return new MASScheduleExtrasModel(model);
				});
			});
		} catch (error) {
			this.failSchedules('mas:MAS_Schedules_Service.loadScheduleExtrasForProgram', error, { programId });
		}
	}

	/** Runs the same query for each Firestore-safe `in` batch and merges by document id. */
	private async queryScheduleDocsInBatches(
		values: string[],
		buildQuery: (batch: string[]) => Query<Schedule, DocumentData>,
	): Promise<Schedule[]> {
		const byId = new Map<string, Schedule>();
		for (const batch of chunkForFirestoreIn(values)) {
			const snap = await getDocs(buildQuery(batch));
			for (const docSnap of snap.docs) {
				const data = docSnap.data();
				const id = data.id ?? docSnap.id;
				if (!byId.has(id)) {
					byId.set(id, data);
				}
			}
		}
		return [...byId.values()];
	}
}
